import { snordeluxeAssets } from '../snordeluxeAssets';
import { initiateMultiplayerBattle } from '../gameplay/game.js';
import { getUser, setUser, showSnordeluxeSelection, updateUsernameDisplay } from './views';
import { updateStats } from './stats';
import { saveUserData } from '../auth/kindeClient';
import { sendEventMessage } from './chat';
import {
    STAT_MAX,
    STAT_UPGRADE_STEP,
    STAT_UPGRADE_MANA_COST,
    STAT_TIER_THRESHOLDS
} from '../gameplay/battle-constants.js';
import { hasDefeated } from './defeatedSnordeluxes.js';

// Each feed costs this many coins and adds this much weight. Cap keeps
// the stomp damage from running away (battle-physics caps the multiplier
// at JUMP_WEIGHT_MULT_CAP × base anyway, but the cap below also keeps
// the displayed number tidy).
const FEED_COST_COINS = 50;
const FEED_WEIGHT_GAIN = 2;
const MAX_WEIGHT_BONUS = 60;

// Stamina/Power/Weight stats live on each inventory item. Existing
// inventory entries (created before this system) lack the fields, so
// every read goes through this normalizer so 'undefined' reads as 0.
const STAT_KEYS = ['staminaStat', 'powerStat', 'weightStat'];
function statValue(item, key) {
    return Math.max(0, Math.min(STAT_MAX, Number(item && item[key]) || 0));
}
function getStats(item) {
    return {
        stamina: statValue(item, 'staminaStat'),
        power: statValue(item, 'powerStat'),
        weight: statValue(item, 'weightStat'),
    };
}

// Returns the highest tier (1-based index into STAT_TIER_THRESHOLDS)
// the snordeluxe currently qualifies for — i.e. the largest threshold
// it has met on ALL three stats. 0 = no tier reached yet.
function currentTier(item) {
    const s = getStats(item);
    const min = Math.min(s.stamina, s.power, s.weight);
    let tier = 0;
    for (let i = 0; i < STAT_TIER_THRESHOLDS.length; i++) {
        if (min >= STAT_TIER_THRESHOLDS[i]) tier = i + 1;
    }
    return tier;
}
function tierLabel(tier) {
    if (tier === 0) return 'Base';
    if (tier === 1) return 'Upgraded';
    if (tier === 2) return 'Evolved';
    return 'Max';
}

function totalWeight(snordeluxe) {
    const base = (snordeluxeAssets[snordeluxe.name]?.weight) || 20;
    const bonus = snordeluxe.weightBonus || 0;
    const upgrade = statValue(snordeluxe, 'weightStat');
    return base + bonus + upgrade;
}

function updateInventoryDisplay() {
    console.log("Updating inventory display");
    const inventoryContainer = document.querySelector('.inventory');
    inventoryContainer.innerHTML = '';

    const user = getUser();

    user.inventory.forEach(snordeluxe => {
        const snordeluxeDetails = snordeluxeAssets[snordeluxe.name];
        const itemDiv = document.createElement('div');
        itemDiv.classList.add('inventory-item');
        const tier = currentTier(snordeluxe);
        itemDiv.innerHTML = `
            <img src="${snordeluxeDetails.image}" alt="${snordeluxe.name}">
            <p>${snordeluxe.name}</p>
            <p class="snordeluxe-weight">Weight: ${totalWeight(snordeluxe)}</p>
            ${tier > 0 ? `<span class="tier-badge tier-${tier}" title="${tierLabel(tier)}">${tierLabel(tier)}</span>` : ''}
        `;
        itemDiv.addEventListener('click', () => displaySnordeluxeDetails(snordeluxe));
        inventoryContainer.appendChild(itemDiv);
    });
}

// Build the HTML for one stat row (stamina/power/weight) inside the
// snordeluxe details modal. The upgrade button stays disabled when the
// stat is maxed or when the user can't afford another upgrade.
function statRowHTML(label, statKey, value, manaAvailable) {
    const pct = Math.round((value / STAT_MAX) * 100);
    const maxed = value >= STAT_MAX;
    const canAfford = manaAvailable >= STAT_UPGRADE_MANA_COST;
    const disabledAttr = (maxed || !canAfford) ? 'disabled' : '';
    const buttonLabel = maxed ? 'MAX' : `+${STAT_UPGRADE_STEP} (${STAT_UPGRADE_MANA_COST} mana)`;
    return `
        <div class="stat-row" data-stat="${statKey}">
            <div class="stat-row-head">
                <span class="stat-label">${label}</span>
                <span class="stat-value">${value} / ${STAT_MAX}</span>
            </div>
            <div class="stat-bar"><div class="stat-bar-fill" style="width:${pct}%"></div></div>
            <button class="stat-upgrade-btn" data-stat="${statKey}" ${disabledAttr}>${buttonLabel}</button>
        </div>
    `;
}

function displaySnordeluxeDetails(snordeluxe) {
    const snordeluxeDetails = snordeluxeAssets[snordeluxe.name];
    const user = getUser();
    const stats = getStats(snordeluxe);
    const tier = currentTier(snordeluxe);
    const nextThreshold = STAT_TIER_THRESHOLDS.find(t => t > Math.min(stats.stamina, stats.power, stats.weight)) || null;
    const evolvesInto = snordeluxeDetails.evolution || null;

    const modal = document.createElement('div');
    modal.classList.add('modal-overlay', 'show');
    modal.innerHTML = `
        <div class="modal-content">
            <span class="close-button">&times;</span>
            <img src="${snordeluxeDetails.image}" alt="${snordeluxe.name}" class="snordeluxe-detail-image">
            <div class="snordeluxe-details">
                <h2>${snordeluxe.name} ${tier > 0 ? `<span class="tier-badge tier-${tier}">${tierLabel(tier)}</span>` : ''}</h2>
                <div class="stats-analysis">
                    <p><strong>Level:</strong> ${snordeluxeDetails.level}</p>
                    <p><strong>Hitpoints:</strong> ${snordeluxeDetails.hitpoints}</p>
                    <p><strong>Total Weight:</strong> ${totalWeight(snordeluxe)}${snordeluxe.weightBonus ? ` <span class="weight-bonus">(+${snordeluxe.weightBonus} fed)</span>` : ''}</p>
                </div>

                <div class="upgrade-panel">
                    <p class="upgrade-panel-heading"><strong>Stats</strong> ${nextThreshold !== null && evolvesInto ? `<span class="upgrade-hint">Hit ${nextThreshold} on all 3 stats → evolves to <em>${evolvesInto}</em>${hasDefeated(user, evolvesInto) ? '' : ` <span class="upgrade-hint-locked">(must defeat one in battle first)</span>`}</span>` : (evolvesInto ? '' : '<span class="upgrade-hint">Final form</span>')}</p>
                    ${statRowHTML('Stamina (Run Speed)', 'staminaStat', stats.stamina, user.mana)}
                    ${statRowHTML('Power (Attack DMG)', 'powerStat', stats.power, user.mana)}
                    ${statRowHTML('Weight (Stomp DMG)', 'weightStat', stats.weight, user.mana)}
                </div>

                <p><strong>Attacks:</strong></p>
                <div class="attack">
                    <span>${snordeluxeDetails.attacks.basic.label}: ${snordeluxeDetails.attacks.basic.damage} damage (Probability: ${snordeluxeDetails.attacks.basic.probability}) - ${snordeluxeDetails.attacks.basic.description}</span>
                </div>
                <div class="attack">
                    <span>${snordeluxeDetails.attacks.special.label}: ${snordeluxeDetails.attacks.special.damage} damage (Probability: ${snordeluxeDetails.attacks.special.probability}) - ${snordeluxeDetails.attacks.special.description}</span>
                    ${snordeluxe.trainedAttacks && snordeluxe.trainedAttacks.includes('special') ? '' : `<button class="train-button">${snordeluxeDetails.attacks.special.training_cost} Gems</button>`}
                </div>
                <div class="buttons-row">
                    <button class="multiplayer-battle-button"><i class="fas fa-users"></i> Battle!</button>
                    <button class="feed-button" ${(snordeluxe.weightBonus || 0) >= MAX_WEIGHT_BONUS ? 'disabled' : ''}>
                        <i class="fas fa-drumstick-bite"></i> Feed (${FEED_COST_COINS} coins)
                    </button>
                </div>
            </div>
        </div>
    `;

    document.body.appendChild(modal);

    modal.querySelector('.close-button').addEventListener('click', () => modal.remove());

    const mpButton = modal.querySelector('.multiplayer-battle-button');
    if (mpButton) {
        mpButton.addEventListener('click', () => {
            modal.remove();
            initiateMultiplayerBattle(snordeluxe.name);
        });
    }

    // Stat upgrade buttons (stamina/power/weight). Each click costs
    // STAT_UPGRADE_MANA_COST mana and adds STAT_UPGRADE_STEP to the
    // chosen stat. On a successful upgrade we re-open the modal so the
    // value, mana display, and tier badge all reflect the new state.
    modal.querySelectorAll('.stat-upgrade-btn').forEach(btn => {
        btn.addEventListener('click', async () => {
            const statKey = btn.dataset.stat;
            const ok = await upgradeStat(snordeluxe.name, statKey);
            if (ok) {
                modal.remove();
                const refreshed = getUser().inventory.find(s => s.name === snordeluxe.name);
                // upgradeStat may rename the inventory item (evolution),
                // so look it up by the post-upgrade name too.
                const evolvedRefreshed = !refreshed
                    ? getUser().inventory.find(s => s.name === snordeluxe.name)
                    : refreshed;
                if (evolvedRefreshed) displaySnordeluxeDetails(evolvedRefreshed);
            }
        });
    });

    modal.querySelector('.train-button')?.addEventListener('click', () => {
        trainAttack(snordeluxe.name, 'special');
        modal.remove();
    });

    modal.querySelector('.feed-button')?.addEventListener('click', async () => {
        const ok = await feedSnordeluxe(snordeluxe.name);
        if (ok) {
            modal.remove();
            const refreshed = getUser().inventory.find(s => s.name === snordeluxe.name);
            if (refreshed) displaySnordeluxeDetails(refreshed);
        }
    });

    modal.addEventListener('click', (event) => {
        if (event.target === modal) modal.remove();
    });
}

// Spend STAT_UPGRADE_MANA_COST mana to raise one stat by STAT_UPGRADE_STEP.
// On a successful upgrade we also check whether the snordeluxe crossed
// a tier threshold (all 3 stats ≥ STAT_TIER_THRESHOLDS[n]) and, if so,
// auto-evolve it to its next form once per crossing. Stats persist
// across evolutions so progress toward the next tier continues from
// where it was. Returns true on success, false otherwise.
async function upgradeStat(snordeluxeName, statKey) {
    if (!STAT_KEYS.includes(statKey)) {
        console.warn(`[upgradeStat] unknown stat key: ${statKey}`);
        return false;
    }
    const user = getUser();
    const index = user.inventory.findIndex(s => s.name === snordeluxeName);
    if (index === -1) return false;
    const item = user.inventory[index];
    const current = statValue(item, statKey);
    if (current >= STAT_MAX) {
        displayModal('warning', `${statKey.replace('Stat', '')} is already maxed.`);
        return false;
    }
    if ((user.mana || 0) < STAT_UPGRADE_MANA_COST) {
        displayModal('warning', `Not enough mana (need ${STAT_UPGRADE_MANA_COST}).`);
        return false;
    }

    const tierBefore = currentTier(item);
    user.mana -= STAT_UPGRADE_MANA_COST;
    item[statKey] = Math.min(STAT_MAX, current + STAT_UPGRADE_STEP);

    // Tier-up check. If the min-of-three crossed a threshold this
    // upgrade, walk the snordeluxe's evolution chain once per crossed
    // tier (so a single upgrade can never skip more than one form;
    // the user has to keep upgrading to chain further).
    const tierAfter = currentTier(item);
    let evolvedSummary = null;
    let evolutionBlocked = null; // name of next form the user hasn't defeated yet
    if (tierAfter > tierBefore) {
        const tiersCrossed = tierAfter - tierBefore;
        const evolutions = [];
        for (let i = 0; i < tiersCrossed; i++) {
            const currentAssets = snordeluxeAssets[item.name];
            if (currentAssets && currentAssets.evolution && snordeluxeAssets[currentAssets.evolution]) {
                const toName = currentAssets.evolution;
                // Evolve-gate: the user must have defeated this form
                // in battle at least once (same unlock system the shop
                // uses). Without this, every upgrade auto-evolves with
                // no progression requirement. The stat boost still
                // applies — only the transform is blocked.
                if (!hasDefeated(user, toName)) {
                    evolutionBlocked = toName;
                    break;
                }
                const fromName = item.name;
                item.name = toName;
                // New form starts only with 'basic' attack trained, like
                // the legacy evolve flow did. Special attacks need to be
                // re-trained with gems on the new form.
                item.trainedAttacks = ['basic'];
                evolutions.push({ from: fromName, to: toName });
            } else {
                break; // no further evolution chain → stop climbing
            }
        }
        if (evolutions.length > 0) {
            evolvedSummary = evolutions;
        }
    }

    setUser(user);
    updateStats(user.mana, user.coins, user.gems);
    // Persist best-effort. Earlier this was awaited without a guard, so
    // a backend rejection (e.g. the new stat columns being unknown to
    // the server) threw out of upgradeStat and the calling click
    // handler never reached the modal-refresh code — the upgrade
    // appeared to "do nothing" to the user even though local state
    // had already mutated. Swallow the error so the UI flow continues;
    // the local mutation will be retried on the next save.
    saveUserData(user).catch(err => {
        console.error('[upgradeStat] saveUserData failed (state kept locally):', err);
    });

    if (evolvedSummary) {
        const last = evolvedSummary[evolvedSummary.length - 1];
        const msg = `${user.username}'s ${evolvedSummary[0].from} evolved to ${last.to}!`;
        // Fire-and-forget the chat broadcast — the upgrade flow doesn't
        // depend on the message landing, and awaiting fetch can stall
        // the modal re-render in test environments where the backend is
        // unreachable.
        sendEventMessage(user.username, msg, 'training').catch(() => {});
        console.log('[inventory]', msg);
    }
    if (evolutionBlocked) {
        displayModal('warning',
            `Stats raised, but ${item.name} can't evolve into ${evolutionBlocked} until you defeat one in battle first.`);
    }
    updateInventoryDisplay();
    return true;
}

// Legacy export kept for callers that may still reference it. New flow
// is upgradeStat-driven; this function is now a no-op shim that just
// returns without doing anything.
async function evolveSnordeluxe(_snordeluxeName) {
    console.warn('[inventory] evolveSnordeluxe is deprecated; upgrades happen via upgradeStat.');
}

async function trainAttack(snordeluxeName, attackName) {
    const snordeluxe = snordeluxeAssets[snordeluxeName];
    const user = getUser();
    const attack = snordeluxe.attacks[attackName];

    if (user.gems < attack.training_cost) {
        displayModal('warning', 'Not enough gems to train this attack.');
        return;
    }

    if (confirm(`Do you want to train ${attackName} for ${attack.training_cost} gems?`)) {
        user.gems -= attack.training_cost;
        const snordeluxeInInventory = user.inventory.find(snordeluxe => snordeluxe.name === snordeluxeName);
        snordeluxeInInventory.trainedAttacks.push(attackName);
        setUser(user);
        updateStats(user.mana, user.coins, user.gems);
        await saveUserData(user);
        const trainingMessage = `${user.username}'s ${snordeluxe.name} learned a new attack: ${attackName}!`;
        await sendEventMessage(user.username, trainingMessage, 'training');
        updateInventoryDisplay();
        displayModal('success', `Successfully trained ${attackName}!`);
    }
}

async function feedSnordeluxe(snordeluxeName) {
    const user = getUser();
    const item = user.inventory.find(s => s.name === snordeluxeName);
    if (!item) return false;

    const currentBonus = item.weightBonus || 0;
    if (currentBonus >= MAX_WEIGHT_BONUS) {
        displayModal('warning', `${snordeluxeName} is already at max weight bonus (+${MAX_WEIGHT_BONUS}).`);
        return false;
    }
    if ((user.coins || 0) < FEED_COST_COINS) {
        displayModal('warning', `Not enough coins to feed (need ${FEED_COST_COINS}).`);
        return false;
    }

    user.coins -= FEED_COST_COINS;
    item.weightBonus = currentBonus + FEED_WEIGHT_GAIN;

    setUser(user);
    updateStats(user.mana, user.coins, user.gems);
    await saveUserData(user);
    updateInventoryDisplay();
    return true;
}

function displayModal(type, message) {
    console.log(`MODAL (${type}): ${message}`);
    alert(`${type.toUpperCase()}: ${message}`);
}

export { updateInventoryDisplay, evolveSnordeluxe, trainAttack, feedSnordeluxe, upgradeStat, currentTier, tierLabel };
